import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Field,
  LinkFieldValue,
  RichText,
  WithSitecoreContextProps,
} from '@sitecore-jss/sitecore-jss-react';
import './index.scss';
import Checkbox from '@mui/material/Checkbox';
import { AiOutlineShoppingCart } from 'react-icons/ai';
import { LinkRenderer } from '../../LinkRenderer/LinkRenderer';
import CustomTooltip from '../../CustomTooltip/CustomTooltip';
import { orderSummaryCheckoutDetails } from './IOrderSummaryCheckout';
import { getAPIAsyncCall, getAPICall } from '../../../dataFetcher/axiosMethodCalls';
import { getCurrencyInUSD } from '../../../utils/helper';
import PlaceOrderProcess from '../../PlaceOrderProcess/PlaceOrderProcess';
import LoadingSkeleton from '../../LoadingSkeleton/LoadingSkeleton';
import { APIWebstoreContext } from '../../../context/globalContext';
import { ITaxExemptStatusDetails } from '../../AccountInfo/TaxExempt';
import TaxExemptStatus from '../../TaxExemptStatus/TaxExemptStatus';
import ToastNotification from '../../ToastNotification/ToastNotification';
import { getCountryConfigFields } from '../../../utils/countryConfigurationHelper';
import { ProcessOrderValidationService } from '../../CheckoutPage/ProcessOrderValidationService';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LOB_LIST } from '../../../context/reducer';
import { OrderSummaryDetails } from '../OrderSummary/OrderSummary';
import { ICP_IDENTIFIER } from '../../../utils/constants';
import { PaymentTypes } from '../../../utils/PaymentTypes';

export type OrderSummaryCheckoutProps = WithSitecoreContextProps & {
  fields: {
    Link: Field<LinkFieldValue>;
    TermsAgreementMessage: Field<string>;
    TermsAgreementTooltipText: Field<string>;
    MemberDiscountTooltipText: Field<string>;
    MemberDiscountTitle: Field<string>;
    TaxExemptTooltipText: Field<string>;
    TermsAgreementNoEula: Field<string>;
    GroupPaymentTag: Field<string>;
    TotalDueTag: Field<string>;
    ZeroCostOrderButtonText: Field<string>;
    DisclaimerText: Field<string>;
    ToastMessage: Field<string>;
    ToastMessagePayFabric: Field<string>;
    Countries: Array<OrderSummaryCountryFieldProps>;
    RestrictedCountries: Array<CountryFieldProps>;
    LOBSpecificConfiguration: Array<OrderSummaryLOBFieldProps>;
    Language: Field<string>;
    TaxExemptPendingText: Field<string>;
    TaxExemptLabel: Field<string>;
    TaxExemptProceedText: Field<string>;
    TaxExemptReturnText: Field<string>;
    PaymentAttemptText: Field<string>;
  };
};

export type CountryFieldProps = {
  displayName: string;
  fields: ICountryFields;
};

export interface ICountryFields {
  CountryName: Field<string>;
  CountryCode: Field<string>;
}

export interface IOrderSummaryConfigurationInfo {
  CountryCode: string;
  CountryName: string;
  ShowAccordion: boolean;
  ShowShipping: boolean;
  ShowSalesTax: boolean;
  ShowMemberDiscount: boolean;
  ShowEula: boolean;
  ShowTaxExempt: boolean;
  SalesTaxMessage: string;
  OrderSummaryBtnText: string;
  Language: string;
}

export type OrderSummaryCountryFieldProps = {
  displayName: string;
  fields: {
    ShowAccordion: Field<boolean>;
    ShowShipping: Field<boolean>;
    ShowSalesTax: Field<boolean>;
    ShowMemberDiscount: Field<boolean>;
    ShowEula: Field<boolean>;
    ShowTaxExempt: Field<boolean>;
    CountryCode: Field<string>;
    CountryName: Field<string>;
    SalesTaxMessage: Field<string>;
    OrderSummaryBtnText: Field<string>;
    Language: Field<string>;
  };
};

export type OrderSummaryLOBFieldProps = {
  displayName: string;
  fields: {
    ShowAccordion: Field<boolean>;
    ShowShipping: Field<boolean>;
    ShowSalesTax: Field<boolean>;
    ShowMemberDiscount: Field<boolean>;
    ShowEula: Field<boolean>;
    ShowTaxExempt: Field<boolean>;
    LOBIdentifier: Field<string>;
    SectionTitle: Field<string>;
  };
};

export interface CalculateErrors {
  orderId: string;
  errorCode: string;
  errorMessage: string;
}

const OrderSummaryCheckout = ({ fields }: OrderSummaryCheckoutProps): React.JSX.Element => {
  const [IsUSOnly] = useState(true);
  const [orderSummaryDetails, setOrderSummaryDetails] = useState<{
    [lob: string]: OrderSummaryDetails;
  }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isTaxLoading, setIsTaxLoading] = useState(true);
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [isPrivacyPolicyAgreed, setIsPrivacyPolicyAgreed] = useState(false);
  const [taxExemptStatusDetails, setTaxExemptStatusDetails] = useState<ITaxExemptStatusDetails>();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<Field<string>>();
  const [customToastMessage, setCustomToastMessage] = useState(fields.ToastMessage);
  const [countryInfo, setCountryInfo] = useState('');
  const [expandedLob, setExpandedLob] = useState<string | null>(null);
  const [calculateErrors, setCalculateErrors] = useState<CalculateErrors[]>([]);

  let OrderSummaryConfig: IOrderSummaryConfigurationInfo;

  const cartLobs = Object.keys(state?.cartCounts?.lobCounts || {}).filter(
    (lob) => (state?.cartCounts?.lobCounts?.[lob] ?? 0) > 0
  );

  const showEULABlock = fields?.LOBSpecificConfiguration?.some(
    (lobConfig) =>
      cartLobs.includes(lobConfig?.fields?.LOBIdentifier?.value) &&
      lobConfig?.fields?.ShowEula?.value
  );

  const fetchData = async () => {
    const lobTotals: { [key: string]: number } = {};
    let orderSummaryErrors = [];
    const orderSummaryDetails: { [lob: string]: OrderSummaryDetails } = {};
    for (const lob of LOB_LIST) {
      try {
        const response: any = await getAPICall(
          `${process.env.REACT_APP_CART_URL}/checkout/ordersummary?lob=${lob}`
        );
        if (response && response.data) {
          lobTotals[lob] = response.data.total ?? 0;
          orderSummaryDetails[lob] = response.data;

          if (response.data.errors.length > 0) {
            orderSummaryErrors.push(...response.data.errors);
          }
        }
      } catch (error) {
        console.error(`Error fetching order summary for ${lob}:`, error);
        lobTotals[lob] = 0;
      }
    }
    setOrderSummaryDetails(orderSummaryDetails);
    setCalculateErrors(orderSummaryErrors);
    const totalAmount = Object.values(lobTotals).reduce((sum, val) => sum + val, 0);
    if (dispatch) {
      dispatch({
        type: 'CHECKOUT_UPDATE',
        payload: {
          orderTotals: {
            totalAmount,
            lobTotals,
          },
        },
      });
    }
    setIsLoading(false);
    setIsTaxLoading(false);
  };

  async function getShippingCountry() {
    let shippingCountry = null;
    try {
      const response = await getAPIAsyncCall(
        `${process.env.REACT_APP_CART_URL}/checkout/getshippingcountry`
      );
      if (response && response.data) {
        shippingCountry = response.data;
      }
    } catch (error: any) {
      shippingCountry = null;
    } finally {
      if (!shippingCountry) {
        shippingCountry = ProcessOrderValidationService.getShippingInfo()?.shippingInfo?.country;
      }
      setCountryInfo(shippingCountry);
    }
    return shippingCountry;
  }

  function getTaxExemptStatusApiCall() {
    getAPICall(`${process.env.REACT_APP_PROFILE_URL}/profile/taxexemptstatus`).then(function (
      response: any
    ) {
      if (response && response.data) {
        setTaxExemptStatusDetails(response.data);
        if (response.data.IsTaxExemptPending && !shouldSkipTaxExempt) {
          showCustomToast(
            state?.sitecoreDictionaryData
              ? state?.sitecoreDictionaryData.TaxExemptPendingMessage
              : 'Your checkout is disabled until tax exempt verification is completed. It takes us about 2-3 days to get the verification completed.'
          );
        }
      }
    });
  }

  useEffect(() => {
    fetchData();
    if (dispatch) {
      dispatch({
        type: 'CHECKOUT_UPDATE',
        payload: {
          isGroupPaymentSuccessful: false,
        },
      });
    }
  }, [state?.toggleOrderSummaryLoad]);

  useEffect(() => {
    setIsTaxLoading(true);
    const asycService = async () => {
      await getShippingCountry();
      fetchData();
      getTaxExemptStatusApiCall();
    };
    asycService();
  }, [state?.toggleCalculateOrder]);

  useEffect(() => {
    dispatch?.({
      type: 'CHECKOUT_UPDATE',
      payload: { hasEULA: showEULABlock },
    });
  }, [showEULABlock]);

  const toastRemove = () => {
    setShowToast(false);
  };

  const showCustomToast = (message: string) => {
    customToastMessage.value = message;
    setToastMessage(customToastMessage);
    setShowToast(true);
  };

  function renderWithLoading(value: any, theme?: any, loaderWidth?: any, loaderHeight?: any) {
    return isLoading ? (
      <LoadingSkeleton theme={theme} loaderWidth={loaderWidth} loaderHeight={loaderHeight} />
    ) : (
      value
    );
  }

  function renderWithTaxLoading(value: any, theme?: any) {
    return isTaxLoading ? <LoadingSkeleton theme={theme} /> : value;
  }

  const shouldSkipTaxExempt = fields?.LOBSpecificConfiguration?.some(
    (lobConfig) =>
      cartLobs.includes(lobConfig.fields.LOBIdentifier.value) &&
      !lobConfig?.fields?.ShowTaxExempt?.value
  );

  const hasTax = fields?.LOBSpecificConfiguration?.some(
    (lobConfig) =>
      cartLobs.includes(lobConfig.fields.LOBIdentifier.value) &&
      lobConfig?.fields?.ShowTaxExempt?.value
  );

  function getSalesTax(
    IsUSOnly: boolean,
    orderSummaryConfigurationInfo: IOrderSummaryConfigurationInfo | undefined,
    lob: string
  ) {
    if (!IsUSOnly) {
      return renderOrderSummaryRow(
        'VAT/GST',
        Number(orderSummaryCheckoutDetails?.orderSalesTax),
        'orderSalesTax'
      );
    }

    const hasTaxStatus =
      !fields?.RestrictedCountries?.some((x) => x.fields.CountryCode.value === countryInfo) &&
      orderSummaryConfigurationInfo?.ShowTaxExempt &&
      (taxExemptStatusDetails?.IsTaxExempt ||
        (taxExemptStatusDetails?.IsTaxExemptPending && !shouldSkipTaxExempt));

    return (
      <div className={hasTaxStatus ? 'salesTaxStatus' : 'salesTax'}>
        {orderSummaryConfigurationInfo?.ShowSalesTax && (
          <div className="orderSalesTaxNotUS orderSummaryAlign">
            <p className="orderSalesTaxDetailNotUS">Sales Tax:</p>
            {!hasTaxStatus && (
              <p className="orderSalesTaxDetailNotUS">
                {(orderSummaryConfigurationInfo?.SalesTaxMessage?.length ?? 0) > 0
                  ? orderSummaryConfigurationInfo?.SalesTaxMessage
                  : renderWithTaxLoading(
                      getCurrencyInUSD(orderSummaryDetails?.[lob]?.taxTotal),
                      'dark'
                    )}
              </p>
            )}
          </div>
        )}

        {orderSummaryConfigurationInfo?.ShowTaxExempt && (
          <div className="orderSummaryAlign taxExemptBorder">
            <TaxExemptStatus
              fields={{
                TaxExemptTooltip: fields?.TaxExemptTooltipText,
              }}
              props={{
                TaxExemptStatusDetails: taxExemptStatusDetails,
                hideTaxExempt: hasTaxStatus,
              }}
            />
          </div>
        )}
      </div>
    );
  }

  function getOrderSummaryConfiguration(
    lob: string,
    shippingCountry: string
  ): IOrderSummaryConfigurationInfo | undefined {
    const countryConfigurationData = getCountryConfigFields(shippingCountry, fields.Countries);

    const lobSpecificConfig = getLOBConfig(lob);
    if (!lobSpecificConfig) return undefined;

    const countrySpecificConfig = countryConfigurationData[0].fields;

    OrderSummaryConfig = {
      CountryCode: countrySpecificConfig?.CountryCode,
      CountryName: countrySpecificConfig?.CountryName,
      ShowAccordion: lobSpecificConfig?.fields?.ShowAccordion?.value,
      ShowShipping:
        lobSpecificConfig?.fields?.ShowShipping?.value &&
        countrySpecificConfig?.ShowShipping?.value,
      ShowSalesTax:
        lobSpecificConfig?.fields?.ShowSalesTax?.value &&
        countrySpecificConfig?.ShowSalesTax?.value,
      ShowMemberDiscount:
        lobSpecificConfig.fields?.ShowMemberDiscount?.value &&
        countrySpecificConfig?.ShowMemberDiscount?.value,
      ShowEula:
        lobSpecificConfig?.fields?.ShowEula?.value && countrySpecificConfig?.ShowEula?.value,
      ShowTaxExempt:
        lobSpecificConfig?.fields?.ShowTaxExempt?.value &&
        countrySpecificConfig?.ShowTaxExempt?.value,
      SalesTaxMessage: countrySpecificConfig?.SalesTaxMessage,
      OrderSummaryBtnText: countrySpecificConfig?.OrderSummaryBtnText?.value,
      Language: countrySpecificConfig?.Language?.value,
    };

    return OrderSummaryConfig;
  }

  function renderOrderSummaryRow(label: string, amount?: number, mainClass = 'orderSubTotal') {
    return (
      <div className={`${mainClass}`}>
        <p className={`${mainClass}Detail`}>{label}</p>
        <p className={`${mainClass}Detail`}>
          {renderWithLoading(getCurrencyInUSD(amount), 'dark', '90px', '25px')}
        </p>
      </div>
    );
  }

  function getLOBConfig(lob: string): OrderSummaryLOBFieldProps | undefined {
    return fields.LOBSpecificConfiguration.find((x) => x.fields.LOBIdentifier.value === lob);
  }

  const lobParam = useMemo(() => {
    const cartLOBs = state?.cartCounts?.lobCounts;

    for (const lob in cartLOBs) {
      if (cartLOBs[lob] > 0) return lob;
    }
    return null;
  }, [state?.cartCounts?.lobCounts]);

  function getOrderSummary() {
    return (
      <div className="orderSummaryCheckout">
        <h1>Order Summary</h1>
        <div className="orderSummaryEditCart">
          <AiOutlineShoppingCart />
          <LinkRenderer
            fields={{
              Link: {
                value: {
                  ...fields.Link.value,
                  querystring: lobParam ? `lob=${lobParam}` : '',
                },
              },
            }}
          />
        </div>

        {cartLobs.map((lob) => {
          const lobConfig = fields?.LOBSpecificConfiguration?.find(
            (x) => x.fields.LOBIdentifier.value === lob
          );

          const isExpanded = expandedLob === lob;

          const orderSummaryConfig = getOrderSummaryConfiguration(lob, countryInfo);

          return (
            <React.Fragment key={lob}>
              {state?.isGroupPaymentSuccessful && (
                <div className="group-payment-success">
                  {lob === ICP_IDENTIFIER
                    ? fields?.GroupPaymentTag?.value
                    : fields?.TotalDueTag?.value}
                </div>
              )}

              {orderSummaryConfig?.ShowTaxExempt &&
                shouldSkipTaxExempt &&
                taxExemptStatusDetails?.IsTaxExemptPending && (
                  <div className="tax-exempt-pending">{fields?.TaxExemptLabel?.value}</div>
                )}

              {orderSummaryConfig?.ShowAccordion ? (
                <Accordion
                  className="order-summary-accordion"
                  expanded={isExpanded}
                  onChange={() => setExpandedLob((prev) => (prev === lob ? null : lob))}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    id={`${lob}-header`}
                    className="accordion-header"
                  >
                    <div className="accordionHeader">
                      <p className="accordion-title">{lobConfig?.fields?.SectionTitle?.value}</p>
                      {!isExpanded && (
                        <div className="orderPriceDetails">
                          {renderOrderSummaryRow(
                            'Product Total:',
                            orderSummaryDetails?.[lob]?.total
                          )}
                        </div>
                      )}
                    </div>
                  </AccordionSummary>
                  <hr className="accordion-line" />
                  <AccordionDetails className="accordion-details">
                    <div className="orderPriceDetails">
                      {renderOrderSummaryRow(
                        'Product Subtotal:',
                        orderSummaryDetails?.[lob]?.subTotal
                      )}
                      {orderSummaryConfig?.ShowMemberDiscount && (
                        <div className="orderMemberDiscount orderSummaryAlign">
                          <div className="orderMemberDiscountTitleSection">
                            <p>{fields?.MemberDiscountTitle?.value}</p>
                            <CustomTooltip
                              fields={{ 'Tooltip Text': fields?.MemberDiscountTooltipText }}
                            />
                          </div>
                          <p className="member-discount-value">
                            {renderWithLoading(
                              getCurrencyInUSD(orderSummaryDetails?.[lob]?.memberDiscount),
                              'light'
                            )}
                          </p>
                        </div>
                      )}
                      {orderSummaryConfig?.ShowShipping && (
                        <div className="orderPriceDetails">
                          {renderOrderSummaryRow(
                            'Shipping and Handling:',
                            orderSummaryDetails?.[lob]?.shippingTotal,
                            'orderShippingAndHandling'
                          )}
                        </div>
                      )}
                      {orderSummaryConfig?.ShowSalesTax &&
                        getSalesTax(IsUSOnly, orderSummaryConfig, lob)}
                    </div>
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Accordion className="order-summary-accordion" expanded={false}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon style={{ visibility: 'hidden' }} />}
                    className="accordion-header"
                  >
                    <div className="accordionHeader">
                      <p className="accordion-title">{lobConfig?.fields?.SectionTitle?.value}</p>
                      <div className="orderPriceDetails">
                        {renderOrderSummaryRow(
                          'Product Total:',
                          state?.orderTotals?.lobTotals?.[lob]
                        )}
                      </div>
                    </div>
                  </AccordionSummary>
                </Accordion>
              )}
            </React.Fragment>
          );
        })}

        <div className="total-section">
          <div className="orderPriceDetails">
            {renderOrderSummaryRow('Total:', state?.orderTotals?.totalAmount, 'orderTotal')}
          </div>
        </div>

        <div className="orderAgreementSection">
          <div className="orderAgreementSectionTitleSection">
            <Checkbox
              color="default"
              onChange={(e) => {
                setIsPrivacyPolicyAgreed(e.target.checked);
              }}
              title={fields?.TermsAgreementTooltipText?.value}
            />
          </div>
          <RichText
            className="orderAgreementText"
            field={showEULABlock ? fields?.TermsAgreementMessage : fields?.TermsAgreementNoEula}
          />
        </div>

        <PlaceOrderProcess
          fields={{
            ToastMessage: fields?.ToastMessage,
            ToastMessagePayFabric: fields?.ToastMessagePayFabric,
            TaxExemptPendingText: fields?.TaxExemptPendingText,
            TaxExemptProceedText: fields?.TaxExemptProceedText,
            TaxExemptReturnText: fields?.TaxExemptReturnText,
            PaymentAttemptText: fields?.PaymentAttemptText,
          }}
          isPrivacyPolicyAgreed={isPrivacyPolicyAgreed}
          orderSummaryDetails={orderSummaryDetails}
          orderSummaryBtnText={
            state?.orderTotals?.totalAmount !== 0 && state?.paymentType === PaymentTypes.CreditCard
              ? OrderSummaryConfig.OrderSummaryBtnText
              : fields?.ZeroCostOrderButtonText?.value
          }
          taxExemptPopUp={
            shouldSkipTaxExempt && taxExemptStatusDetails?.IsTaxExemptPending && hasTax
          }
          taxExemptPending={taxExemptStatusDetails?.IsTaxExemptPending && !shouldSkipTaxExempt}
          isOrderLoading={isLoading}
          restrictedCountries={fields?.RestrictedCountries}
          orderTotal={state?.orderTotals?.totalAmount}
          language={OrderSummaryConfig?.Language}
          calculateErrors={calculateErrors}
        />

        <div className="checkoutDisclaimerSection">
          <RichText field={fields.DisclaimerText} />
        </div>

        <ToastNotification
          fields={{ ToastMessage: toastMessage }}
          showToast={showToast}
          removeToast={toastRemove}
          toastStatus="error"
        />
      </div>
    );
  }
  return <div className="orderSummarySticky">{getOrderSummary()}</div>;
};
export default OrderSummaryCheckout;
